import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import api from '../api';
import { formatDate, GOALS_TEXT_SCOPE_STYLES, prettyLabel, statusStyle } from './shared';

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
`;

const Badge = styled.span`
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dc2626;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid ${(props) => (props.$unread ? '#bfdbfe' : '#e2e8f0')};
  background: ${(props) => (props.$unread ? '#eff6ff' : '#fff')};
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const Type = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) => props.$style.bg};
  color: ${(props) => props.$style.text};
  border: 1px solid ${(props) => props.$style.border};
`;

const Message = styled.div`
  color: #0f172a;
  font-size: 0.88rem;
  line-height: 1.45;
`;

export default function GoalNotificationsPanel({ moduleName = '' }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/goals/notifications', {
        params: moduleName ? { module: moduleName } : {},
      });
      const rawNotifications = response.data?.data || [];
      const filteredNotifications = moduleName
        ? rawNotifications.filter((item) => !item.goal_module || item.goal_module === moduleName || item.goal_module === 'ALL_MODULES')
        : rawNotifications;
      setNotifications(filteredNotifications);
      setUnreadCount(filteredNotifications.filter((item) => !item.read_at).length);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!active) return;
      await loadNotifications();
    };
    run();
    const timer = window.setInterval(run, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [moduleName]);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/goals/notifications/${notificationId}`);
      await loadNotifications();
    } catch {
      // ignore
    }
  };

  return (
    <Panel className="goals-text-scope">
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>
      <Header>
        <Title>{moduleName ? `${prettyLabel(moduleName)} Goal Notifications` : 'Goal Notifications'}</Title>
        {unreadCount > 0 ? <Badge>{unreadCount}</Badge> : null}
      </Header>
      <List>
        {notifications.length ? notifications.slice(0, 6).map((item) => (
          <Item key={item.id} $unread={!item.read_at} onClick={() => markAsRead(item.id)}>
            <Meta>
              <Type $style={statusStyle(item.notification_type?.includes('OVERDUE') ? 'OVERDUE' : item.notification_type?.includes('RISK') ? 'AT_RISK' : 'ON_TRACK')}>
                {prettyLabel(item.notification_type)}
              </Type>
              <span style={{ color: '#000', fontSize: 12 }}>{formatDate(item.created_at)}</span>
            </Meta>
            <Message>{item.message}</Message>
          </Item>
        )) : <div style={{ color: '#000', fontSize: 14 }}>No goal notifications for this module yet.</div>}
      </List>
    </Panel>
  );
}
